import {
  Alert,
  AlertDescription,
  AlertTitle,
  FormControl,
  HStack,
  Icon,
  IconButton,
  Link,
  Text,
  Button as ChakraButton,
  Thead,
  Tr,
  Th,
  Spinner,
  Tbody,
  Td,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Flex,
  useToast,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { Stack } from '@chakra-ui/layout';
import { FaTimes, FaTrash } from 'react-icons/all';
import { useHistory } from 'react-router';
import { useForm } from 'react-hook-form';
import { FiDownload } from 'react-icons/fi';
import download from 'downloadjs';
import { Card } from '~/components/Card';
import { TerminationRequestsContractView } from '~/types/terminationRequests';
import { ROUTER_PATHS } from '~/constants';
import { replaceParameters } from '~/utils/replaceParamaters';
import { InputFloat } from '~/components/InputFloat';
import { Input, StatusBadge, Table } from '~/components';
import { formatCurrency } from '~/utils/formatCurrency';
import { formatInTimeZone } from '~/utils/formatToTimeZone';
import { getContractAgreement } from '~/services';
import { useRoleCan } from '~/hooks';

export const ContractCard: React.VFC<{
  contract: TerminationRequestsContractView;
  removeContract: (contract: TerminationRequestsContractView) => void;
  visualizacao: boolean;
  visualizacaoAnalysis: boolean;
  analisando?: boolean;
  changeContractValue?: (
    value: number,
    field: string,
    idContrato: number,
  ) => void;
  simulacao?: boolean;
}> = ({
  contract,
  removeContract,
  visualizacao,
  visualizacaoAnalysis,
  analisando,
  changeContractValue,
  simulacao,
}) => {
  const toast = useToast();
  const [fechado, setFechado] = useState(false);
  const history = useHistory();
  const { setError, clearErrors, formState } = useForm();
  const { errors } = formState;
  const [contractControl, setContractControl] = useState(contract);
  type FieldKeys = 'aviso_previo' | 'valor_mensal_atual' | 'multa';

  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  const adminCan = useRoleCan({ roles: ['administrador'] });

  const removerDaLista = () => {
    removeContract(contract);
  };

  const handleChange = (e: number, field: FieldKeys) => {
    clearErrors(field);
    const v = e.toString().replace(/\D/g, '');
    if (v.length > 12) {
      return;
    }
    if (!Number(v)) {
      setError(field, {
        type: 'required',
        message: 'O valor deve ser maior que 0',
      });
    }
    setContractControl({ ...contractControl, [field]: e });
    if (changeContractValue) {
      setTimeout(() => {
        changeContractValue(e, field, contract.id_contrato);
      }, 500);
    }
  };

  const formataNumberStrToFloat = (value: string) => {
    const v = value.replace('.', '').replace('R$', '');
    return Number(v.replace(',', '.'));
  };

  const handleDownloadAgreement = async () => {
    setIsLoadingDownload(true);
    getContractAgreement(contract.id_contrato.toString())
      .then(r => {
        if (r.data.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            r.headers['content-disposition'].match(/filename="(.+)"/)[1],
          );
        } else {
          toast({
            title: r?.data?.error,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(async err => {
        // recuperando o erro do back
        // https://github.com/axios/axios/issues/3779
        const error2 = JSON.parse(await err.response.data.text());

        toast({
          title: `Erro`,
          description: 'Arquivo não encontrado na área de Documentos',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => setIsLoadingDownload(false));
  };

  const header = () => {
    return (
      <HStack px={3} justifyContent="space-between">
        <HStack>
          <Text fontWeight="bold">Contrato:</Text>
          <ChakraButton
            variant="link"
            fontWeight="bold"
            color="#5b8af0"
            _hover={{
              color: 'gray.900',
              textDecoration: 'underline',
            }}
            onClick={() =>
              history.push(
                replaceParameters(
                  ROUTER_PATHS.PORTAL.CONCTRACT_DETAIL,
                  String(contract.id_contrato),
                ),
              )
            }
            fontSize="15px"
            textDecoration="underline"
          >
            {contract.nu_contrato}
          </ChakraButton>
        </HStack>
        <HStack gridGap={2}>
          {(analisando || visualizacaoAnalysis) && (
            <HStack>
              <Text fontWeight="bold">Valor total:</Text>
              <Text fontSize="18px" fontWeight="bold">
                {contract.valor_total}
              </Text>
            </HStack>
          )}
          {((!visualizacao && !analisando) || simulacao) && (
            <IconButton
              aria-label="remover"
              icon={<Icon as={FaTrash} />}
              size="sm"
              onClick={removerDaLista}
            />
          )}
        </HStack>
      </HStack>
    );
  };
  const content = () => {
    return (
      <HStack
        justifyContent="space-between"
        p={3}
        alignItems="baseline"
        fontSize="14px"
        lineHeight="1.2rem"
      >
        <Stack w={!analisando ? '70%' : '60%'}>
          <HStack gridGap={4} alignItems="baseline">
            <Text fontWeight="bold" width="30%" minW="30%">
              CNPJ:
            </Text>
            <Text>{contract.cnpj}</Text>
          </HStack>
          <HStack gridGap={4} alignItems="baseline">
            <Text fontWeight="bold" width="30%" minW="30%">
              Empresa:
            </Text>
            <Text>{contract.empresa}</Text>
          </HStack>
          <HStack gridGap={4} alignItems="baseline">
            <Text fontWeight="bold" width="30%" minW="30%">
              Locado por:
            </Text>
            <Text>{contract?.locador || '-'}</Text>
          </HStack>
          <HStack gridGap={4} alignItems="baseline">
            <Text fontWeight="bold" width="30%" minW="30%">
              N° Pedido Locação:
            </Text>
            <Text>{contract.nu_pedido}</Text>
          </HStack>
          <HStack gridGap={4} alignItems="baseline">
            <Text fontWeight="bold" width="30%" minW="30%">
              Vencido há:
            </Text>
            <Text>
              {contract.vencido_desde ? `${contract.vencido_desde}` : '-'}
            </Text>
          </HStack>
          <HStack gridGap={4} alignItems="baseline">
            <Text fontWeight="bold" width="30%" minW="30%">
              Aviso prévio:
            </Text>
            {!analisando && (
              <Text>
                {contract.aviso_previo
                  ? contract.aviso_previo > 1
                    ? `${contract.aviso_previo} meses`
                    : `${contract.aviso_previo} mês`
                  : '-'}
              </Text>
            )}
            {analisando && (
              <FormControl isInvalid={errors.aviso_previo} w="150px">
                <Input
                  isRequired
                  error={errors.aviso_previo}
                  type="text"
                  size="sm"
                  textAlign="right"
                  fontSize="15px"
                  value={contractControl.aviso_previo}
                  name="aviso_previo"
                  onChange={e => {
                    handleChange(Number(e.target.value), 'aviso_previo');
                  }}
                />
              </FormControl>
            )}
          </HStack>

          <Stack>
            {adminCan && (
              <ChakraButton
                variant="link"
                width="fit-content"
                mt="10px"
                disabled={isLoadingDownload}
                rightIcon={<FiDownload />}
                onClick={handleDownloadAgreement}
                color="gray.800"
                background="white"
                _hover={{
                  color: 'gray.900',
                  textDecoration: 'underline',
                }}
              >
                <Text fontWeight="bold">Baixar termo de contratação</Text>
                {isLoadingDownload && (
                  <Spinner style={{ marginLeft: '10px' }} />
                )}
              </ChakraButton>
            )}
          </Stack>
        </Stack>
        <Stack w={!analisando ? '30%' : '40%'}>
          <HStack gridGap={4} justifyContent="space-between">
            <Text fontWeight="bold">Valor mensal atual (R$):</Text>
            {!analisando && (
              <Text fontWeight="bold">{contract.valor_mensal_atual}</Text>
            )}
            {analisando && (
              <FormControl isInvalid={errors.valor_mensal_atual} w="150px">
                <InputFloat
                  isRequired
                  error={errors.valor_mensal_atual}
                  type="text"
                  size="sm"
                  textAlign="right"
                  fontSize="15px"
                  value={
                    typeof contractControl.valor_mensal_atual !== 'number'
                      ? formataNumberStrToFloat(
                          contractControl.valor_mensal_atual.trim(),
                        )
                      : contractControl.valor_mensal_atual
                  }
                  name="valor_mensal_atual"
                  onChangeValue={e => {
                    handleChange(e, 'valor_mensal_atual');
                  }}
                />
              </FormControl>
            )}
          </HStack>
          <HStack gridGap={4} justifyContent="space-between">
            <Text fontWeight="bold">Valor Faturas vencidas:</Text>
            <Text color="red.500" fontWeight="bold">
              {contract.total_faturas_vencidas}
            </Text>
          </HStack>
          {analisando && (
            <HStack gridGap={4} justifyContent="space-between">
              <Text fontWeight="bold">Multa rescisória (%):</Text>
              <FormControl isInvalid={errors.multa} w="150px">
                <InputFloat
                  isRequired
                  error={errors.multa}
                  type="text"
                  size="sm"
                  textAlign="right"
                  fontSize="15px"
                  value={contractControl.multa}
                  name="multa"
                  onChangeValue={e => {
                    handleChange(e, 'multa');
                  }}
                />
              </FormControl>
            </HStack>
          )}
          {visualizacaoAnalysis && (
            <HStack gridGap={4} justifyContent="space-between">
              <Text fontWeight="bold">Multa rescisória (%):</Text>
              <Text fontWeight="bold">
                {contract.multa.toLocaleString('pt-BR')}
              </Text>
            </HStack>
          )}
          {analisando && (
            <HStack gridGap={4} justifyContent="space-between">
              <Text fontWeight="bold">Valor da multa:</Text>
              <Text fontWeight="bold">{contract.valor_multa}</Text>
            </HStack>
          )}
          {visualizacaoAnalysis && (
            <HStack gridGap={4} justifyContent="space-between">
              <Text fontWeight="bold">Valor da multa:</Text>
              <Text fontWeight="bold">{contract.valor_multa}</Text>
            </HStack>
          )}
        </Stack>
      </HStack>
    );
  };

  const footer = () => {
    return (
      <>
        {!analisando && !visualizacaoAnalysis ? (
          <Stack px={3}>
            <Text fontWeight="bold">
              Você possui faturas em aberto: {contract.qtde_faturas_aberto}
            </Text>
          </Stack>
        ) : (
          <Accordion allowMultiple>
            <AccordionItem id={contract.id_contrato.toString()}>
              <AccordionButton p="0px">
                <h2 style={{ width: '100%' }}>
                  <HStack justifyContent="space-between" w="70%">
                    <Flex alignItems="center">
                      <AccordionIcon boxSize="20px" />{' '}
                      <Text p="2" fontWeight="bold" color="#78797A">
                        Últimas faturas:
                      </Text>
                    </Flex>
                    <ChakraButton
                      variant="link"
                      fontWeight="bold"
                      color="#78797A"
                      onClick={() =>
                        history.push(
                          `/portal/invoices/?busca=${contract.nu_contrato}&situacao=2`,
                        )
                      }
                    >
                      Ver faturas vencidas
                    </ChakraButton>
                  </HStack>
                </h2>
              </AccordionButton>
              <AccordionPanel px="2">
                <Table w="100%" minW="600px">
                  <Thead>
                    <Tr>
                      <Th>Nº Fatura</Th>
                      <Th textAlign="right">Valor Total</Th>
                      <Th>Vencimento</Th>
                      <Th>Situação</Th>
                      <Th style={{ textAlign: 'center' }}>Correção inflação</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {contract?.faturas?.length ? (
                      contract.faturas.map(invoice => (
                        <Tr key={invoice.id_fatura} fontSize="14px">
                          <Td>
                            <ChakraButton
                              variant="link"
                              fontWeight="normal"
                              color="#78797A"
                              onClick={() =>
                                history.push(
                                  `/portal/invoices/${invoice.id_fatura}`,
                                )
                              }
                              fontSize="14px"
                            >
                              {invoice.nu_fatura ?? '-'}
                            </ChakraButton>
                          </Td>
                          <Td textAlign="right">
                            {formatCurrency(Number(invoice.vr_fatura))}
                          </Td>
                          <Td>
                            {formatInTimeZone(
                              `${invoice.dt_vencimentofatura}T00:00:00Z`,
                              'dd/MM/yyyy',
                            )}
                          </Td>
                          <Td>
                            <StatusBadge
                              variant={
                                invoice.st_pagamento === '1'
                                  ? 'green'
                                  : invoice.st_pagamento === '2'
                                  ? 'red'
                                  : 'purple'
                              }
                            >
                              {invoice.st_pagamento === '1'
                                ? 'Pago'
                                : invoice.st_pagamento === '2'
                                ? 'Vencida'
                                : 'Em aberto'}
                            </StatusBadge>
                          </Td>
                          <Td style={{ textAlign: 'center' }}>
                            {contract.nm_indicecorrecao ? 'Sim' : 'Não'}
                          </Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan={5} textAlign="center">
                          <Text textAlign="center">Não há faturas.</Text>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        )}
      </>
    );
  };

  return (
    <>
      <Card
        key={contract?.nu_contrato}
        header={header()}
        content={content()}
        footer={footer()}
        width="65%"
        bg="transparent"
        borderTopColor="#9246ce"
        backgroundColor="#fbfafe"
      />
    </>
  );
};
